/**
 * Contratos de datos preliminares (F2).
 *
 * Son el contrato que la aplicación consume, NO el esquema de la base: el datastore es F3
 * y su diseño puede diferir. Declararlos ahora permite que las vistas se construyan contra
 * una frontera estable y que el adaptador real reemplace al mock sin tocar presentación.
 *
 * ⚠️ D-G: en v1 no viajan datos personales. `ownerId` y `clientCode` son referencias opacas.
 */

export type EntityId = string;
export type IsoDate = string;

export type ProjectStatus = 'draft' | 'active' | 'blocked' | 'done' | 'archived';
export type BriefStatus = 'draft' | 'submitted' | 'accepted' | 'rejected';
export type StageState = 'pending' | 'running' | 'blocked' | 'done' | 'skipped';
export type ApprovalDecision = 'approved' | 'rejected' | 'changes-requested';

/** Los nueve estados del pipeline aprobados en la orden de Fase 1. */
export const PIPELINE_STAGES = [
  'brief', 'estrategia', 'direccion-creativa', 'diseno',
  'produccion', 'motion', 'qa', 'aprobacion', 'entrega',
] as const;
export type PipelineStageId = (typeof PIPELINE_STAGES)[number];

export interface Brief {
  readonly id: EntityId;
  readonly code: string;
  readonly title: string;
  readonly need: string;
  readonly status: BriefStatus;
  readonly createdAt: IsoDate;
  readonly ownerId: EntityId;
  /** A quién le habla. Texto libre del usuario, no un id. */
  readonly audience: string;
  /** Para cuándo. Texto libre («en 15 días»), no una fecha. */
  readonly deadline: string;
}

export interface Project {
  readonly id: EntityId;
  readonly code: string;
  readonly name: string;
  readonly status: ProjectStatus;
  readonly briefId: EntityId | null;
  readonly updatedAt: IsoDate;
}

export interface PipelineStage {
  readonly id: PipelineStageId;
  readonly label: string;
  readonly state: StageState;
  readonly projectId: EntityId;
}

export interface Approval {
  readonly id: EntityId;
  readonly projectId: EntityId;
  readonly deliverableId: EntityId | null;
  readonly decision: ApprovalDecision | null;
  readonly requestedAt: IsoDate;
  readonly decidedAt: IsoDate | null;
  readonly decidedBy: EntityId | null;
}

export interface Deliverable {
  readonly id: EntityId;
  readonly projectId: EntityId;
  readonly name: string;
  readonly format: string;
  readonly version: number;
  readonly status: 'wip' | 'review' | 'approved' | 'published';
  readonly updatedAt: IsoDate;
  /** El artefacto generado (HTML de la landing, etc.). `null` si aún no se generó. */
  readonly content: string | null;
  /** El concepto creativo, 1-2 frases. */
  readonly summary: string | null;
  /** Dónde vive el archivo en Drive. Para un video es la ÚNICA forma de abrirlo:
   *  un MP4 no se guarda en la base, así que sin esto la tarjeta queda muerta. */
  readonly driveLink: string | null;
  /** Qué modelo lo produjo. Evidencia de la pieza. */
  readonly model: string | null;
}

/** Estado de integración: DERIVADO de evidencia, nunca declarado a mano (orden §8.6). */
export type IntegrationState =
  | 'connected' | 'available' | 'manual' | 'planned' | 'not-verified' | 'unavailable';

export interface Integration {
  readonly id: EntityId;
  readonly name: string;
  readonly category: string;
  readonly state: IntegrationState;
  /** Sin evidencia enlazable no se afirma «conectada». */
  readonly evidence: string | null;
  readonly checkedAt: IsoDate | null;
}

export interface DecisionRecord {
  readonly id: EntityId;
  readonly ruleId: string;
  readonly ruleVersion: string;
  readonly input: string;
  readonly result: string;
  readonly explanation: string;
  readonly humanOverride: string | null;
  readonly at: IsoDate;
}

export interface ActivityEntry {
  readonly id: EntityId;
  readonly at: IsoDate;
  readonly actorId: EntityId;
  readonly action: string;
  readonly subject: string;
}

export interface DashboardMetrics {
  /** Proyectos en ejecución (plan aprobado). */
  readonly activeProjects: number;
  /** Planes esperando tu aprobación. Antes «briefs pendientes» era esto mismo. */
  readonly pendingApprovals: number;
  /** Etapas bloqueadas: requieren acción inmediata. */
  readonly blockedStages: number;
  readonly recentDeliverables: number;
  readonly failingIntegrations: number;
}

/** Un plan a la espera de aprobación. Alimenta la sección Aprobaciones. */
export interface PlanPendiente {
  readonly proyecto: string;
  readonly pedido: string;
  readonly titulo: string;
  readonly ruta: string;
  readonly entregable: string;
  readonly nivel: string;
  readonly complejidad: 'baja' | 'media' | 'alta';
  readonly plataformas: readonly string[];
  readonly minutos: number;
  readonly justificacion: string | null;
  readonly solicitado: string;
  readonly roadmap: ReadonlyArray<{ readonly label: string; readonly min: number }>;
}

/**
 * El cumplimiento real de los compromisos.
 *
 * La deuda más vieja del sistema: se comprometía una fecha y nunca se medía si
 * se cumplió. Comprometer sin medir es la mitad de la promesa, y es la fácil.
 */
export interface Cumplimiento {
  readonly total: number;
  /** Cuántos ya terminaron. Un proyecto en curso no cumplió ni incumplió. */
  readonly medidos: number;
  readonly aTiempo: number;
  readonly tarde: number;
  /** `null` si no hay nada medido: sin entregas no hay porcentaje que mostrar. */
  readonly porcentaje: number | null;
  readonly proyectos: readonly {
    readonly code: string;
    readonly ruta: string | null;
    readonly comprometido: string | null;
    readonly entregado: string | null;
    readonly estimadoMin: number | null;
    readonly realMin: number | null;
    readonly avance: string;
    readonly cumplio: boolean | null;
  }[];
}
